import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import { CalendarDays, Clock, ArrowRight, Sparkles, Users, Info } from "lucide-react"
import {
  ateliersCollectifsMax,
  brandName,
  calendlyUrl,
  contactPhone,
  planningAteliers,
  serviceArea,
  travelFees,
} from "@/data/site"

function PlanningAteliers() {
  useSeoMeta({
    title: `Planning des ateliers collectifs · ${brandName}`,
    description:
      "Dates des ateliers collectifs : massage bébé, allaitement et portage bébé à Inguiniel (56240). Groupes de 6 personnes maximum, sur réservation.",
    ogTitle: "Planning des ateliers collectifs",
    ogDescription:
      "Ateliers collectifs massage bébé, allaitement et portage bébé à Inguiniel (56240). Groupes de 6 personnes maximum.",
    twitterCard: "summary_large_image",
  })

  return (
    <div className="text-[#2d221b]">
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-10 md:px-8 md:pb-10 md:pt-14">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <CalendarDays size={14} />
          <span>Planning ateliers</span>
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-3xl font-medium leading-tight text-[#2d221b] sm:text-4xl md:text-6xl">
          Les prochains ateliers collectifs
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#5e4b3e] sm:text-base md:text-lg">
          Des rendez-vous en petit groupe pour apprendre, échanger et partager un moment doux autour de bébé.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-[#e3d5c6] bg-[#fbf7f2] px-4 py-3 text-xs text-[#6b5749] sm:text-sm">
          <Users size={16} className="text-[#b85c38]" />
          <span>{ateliersCollectifsMax}</span>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-16 md:px-8">
        <ul className="space-y-5 sm:space-y-6">
          {planningAteliers.map((atelier) => (
            <li
              key={atelier.id}
              className="overflow-hidden rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs transition hover:shadow-warm-md"
            >
              <article className="grid gap-0 md:grid-cols-[220px_1fr]">
                <div className="relative aspect-[16/10] w-full md:aspect-auto md:h-full">
                  <img
                    src={atelier.image}
                    alt={atelier.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="flex flex-col justify-between p-5 sm:p-6 md:p-8">
                  <div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-[#b85c38] px-3 py-1.5 text-[10px] font-semibold uppercase leading-snug tracking-[0.08em] text-white sm:text-[11px] sm:tracking-[0.12em] md:text-xs">
                        {atelier.jour} {atelier.date}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7a6455]">
                        <Clock size={14} className="text-[#b85c38]" />
                        {atelier.horaire}
                      </span>
                    </div>

                    <h2 className="mt-3 font-display text-xl font-medium leading-snug text-[#2d221b] sm:text-2xl md:text-3xl">
                      {atelier.title}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#5e4b3e] md:text-base">
                      {atelier.description}
                    </p>

                    <ul className="mt-4 space-y-1.5">
                      {atelier.infos.map((info) => (
                        <li key={info} className="flex items-start gap-2 text-sm text-[#5e4b3e]">
                          <Info size={14} className="mt-0.5 shrink-0 text-[#b85c38]" />
                          <span>{info}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-5 flex flex-col gap-3 border-t border-[#ece0d2] pt-4 sm:flex-row sm:flex-wrap sm:items-center">
                    {atelier.prix ? (
                      <span className="inline-flex w-fit items-center rounded-full border border-[#d6c4b6] bg-white px-3.5 py-1.5 text-sm font-semibold text-[#2d221b]">
                        {atelier.prix}
                      </span>
                    ) : null}
                    <a
                      href={calendlyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#b85c38] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-warm-sm transition hover:bg-[#9d4c2e] sm:py-2.5"
                    >
                      Réserver
                      <ArrowRight size={14} />
                    </a>
                    {atelier.anchorPath && atelier.anchorId ? (
                      <Link
                        to={`${atelier.anchorPath}#${atelier.anchorId}`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#d6c4b6] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a4225] transition hover:border-[#b85c38] hover:text-[#b85c38] sm:border-0 sm:px-0 sm:py-0 sm:underline sm:decoration-[#d6c4b6] sm:underline-offset-4"
                      >
                        Voir le descriptif
                      </Link>
                    ) : null}
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-3xl border border-[#dfd0c2] bg-[#efe1d4]/80 p-6 text-center shadow-warm-xs sm:p-8 md:mt-12 md:p-10">
          <Sparkles size={20} className="mx-auto text-[#b85c38]" />
          <h2 className="mt-3 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
            Une question sur les ateliers ?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#5e4b3e]">
            {serviceArea} {travelFees}
          </p>
          <p className="mt-2 text-sm text-[#6b5749]">
            Vous pouvez aussi me joindre au{" "}
            <a href={`tel:${contactPhone.replace(/\s/g, "")}`} className="font-semibold text-[#8a4225] underline decoration-[#d6c4b6] underline-offset-4">
              {contactPhone}
            </a>
            .
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#6f4b3e] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#fbf7f2] transition hover:bg-[#5c3d32] sm:w-auto"
          >
            Me contacter
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default PlanningAteliers
